import { useMemo, type JSX } from 'react';
import { Link } from 'react-router-dom';
import { Loader2, MessageCircle, Send, UserCheck, Video } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { useAdminTeams, useTeamPipeline } from '@/hooks/useAdmin';
import { useTeamScope } from '@/hooks/useTeamScope';
import { useUrlState } from '@/hooks/useUrlState';
import { pct } from '@/lib/chart';
import { teamLabel } from '@/lib/labels';
import {
  PIPELINE_PERIODS,
  DEFAULT_PIPELINE_PERIOD,
  countDelta,
  formatDayRange,
  joinDatesCover,
  joinRate,
  JOIN_DATES_NOTE,
  pairPipelineRows,
  parsePipelinePeriod,
  percentDelta,
  pipelineLinks,
  pipelineQueryParams,
  pipelineRanges,
  pipelineTotals,
  rateDelta,
  type Delta,
  type PipelineTeamRow,
} from '@/lib/teamPipeline';
import { cn } from '@/lib/utils';

const NO_MESSAGES_HINT = "This team doesn't record messages";
const HAND_TRACKED_HINT = 'Influencer joins and posts are updated by hand';

/**
 * Home's "Pipeline by team": Messaged → Invites → Joined → Posted for a
 * period, per team, each with its change on the period before. Every count
 * opens the list behind it where one exists.
 */
export function TeamPipeline(): JSX.Element {
  const [url, setUrl] = useUrlState({ period: DEFAULT_PIPELINE_PERIOD as string });
  const period = parsePipelinePeriod(url.period);
  const { team } = useTeamScope();
  const teams = useAdminTeams();

  // Whole UTC days, so the range (and the query key) holds for the day.
  const todayKey = new Date().toISOString().slice(0, 10);
  const ranges = useMemo(
    () => pipelineRanges(period),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [period, todayKey],
  );
  const current = useTeamPipeline(pipelineQueryParams(ranges.current));
  const previous = useTeamPipeline(pipelineQueryParams(ranges.previous));

  const rows = useMemo(
    () => pairPipelineRows(current.data ?? [], previous.data ?? [], team),
    [current.data, previous.data, team],
  );
  const now = pipelineTotals(rows, 'current');
  const before = pipelineTotals(rows, 'previous');
  const anyRecordsMessages = rows.some((r) => r.recordsMessages);
  const anyHandTracked = rows.some((r) => !r.tracksJoins);
  // Join dates only exist from 1 Oct 2026: a period starting earlier can't
  // be compared, and one that straddles it undercounts.
  const joinsComparable = joinDatesCover(ranges.previous);
  const joinsComplete = joinDatesCover(ranges.current);
  const joinedTitle = [JOIN_DATES_NOTE, anyHandTracked ? HAND_TRACKED_HINT : null].filter(Boolean).join(' ');
  // With one team in scope the tiles open that team's lists; across teams
  // they don't, since the Invites page leaves the team sections out.
  const tileLinks = team ? pipelineLinks(team, ranges.current) : null;
  const loading = current.isLoading || previous.isLoading;
  const error = (current.error ?? previous.error) as Error | null;

  const nameOf = (r: PipelineTeamRow): string =>
    teamLabel(r.team, r.displayName ?? teams.data?.find((t) => t.code === r.team)?.displayName);

  return (
    <section aria-labelledby="pipeline-h" className="space-y-3">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h2 id="pipeline-h" className="text-[0.9375rem] font-bold">
            Pipeline by team
          </h2>
          <p className="text-xs text-muted-foreground">
            {formatDayRange(ranges.current)}, compared with {formatDayRange(ranges.previous)}.
          </p>
        </div>
        <div
          role="group"
          aria-label="Period"
          className="inline-flex h-9 items-center rounded-lg border border-input bg-card p-0.5"
        >
          {PIPELINE_PERIODS.map((p) => (
            <button
              key={p.value}
              type="button"
              aria-pressed={period === p.value}
              onClick={() => setUrl({ period: p.value })}
              className={cn(
                'inline-flex h-full items-center rounded-md px-3 text-sm font-medium transition-colors duration-[120ms] pointer-coarse:px-4',
                period === p.value
                  ? 'bg-primary text-primary-foreground'
                  : 'text-muted-foreground hover:text-foreground',
              )}
            >
              {p.label}
            </button>
          ))}
        </div>
      </div>

      {error ? (
        <p role="alert" className="text-sm text-destructive">
          Couldn't load the pipeline: {error.message}
        </p>
      ) : (
        <>
          <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
            <KpiTile
              icon={MessageCircle}
              label="Messages"
              loading={loading}
              value={anyRecordsMessages || loading ? now.messages : null}
              previous={before.messages}
              title={anyRecordsMessages ? undefined : NO_MESSAGES_HINT}
              to={tileLinks?.messages}
            />
            <KpiTile
              icon={Send}
              label="Invites logged"
              loading={loading}
              value={now.invites}
              previous={before.invites}
              to={tileLinks?.invites}
            />
            <KpiTile
              icon={UserCheck}
              label="Joined this period"
              loading={loading}
              value={now.joined}
              previous={joinsComparable ? before.joined : null}
              noComparison="No join dates for the period before"
              hint={
                [joinsComplete ? null : 'Joins before 1 Oct 2026 have no date', anyHandTracked ? 'Not counting Influencer' : null]
                  .filter(Boolean)
                  .join(' · ') || undefined
              }
              title={joinedTitle}
              to={tileLinks?.joined}
            />
            <KpiTile
              icon={Video}
              label="Posted this period"
              loading={loading}
              value={now.posted}
              previous={before.posted}
              hint={anyHandTracked ? 'Not counting Influencer' : undefined}
              title={anyHandTracked ? HAND_TRACKED_HINT : undefined}
              to={tileLinks?.posted}
            />
          </div>

          {loading ? (
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" /> Loading teams…
            </div>
          ) : rows.length === 0 ? (
            <p className="text-sm text-muted-foreground">Nothing recorded for this period yet.</p>
          ) : (
            <div className="table-frame relative overflow-x-auto">
              <table className="responsive-stack w-full text-sm" aria-label="Pipeline by team">
                <thead>
                  <tr className="text-left text-xs font-semibold text-muted-foreground/80">
                    <th className="px-3 py-2 font-semibold">Team</th>
                    <th className="px-3 py-2 text-right font-semibold">Messages</th>
                    <th className="px-3 py-2 text-right font-semibold">Invites</th>
                    <th
                      className="px-3 py-2 text-right font-semibold"
                      title={`Creators who joined in this period, by join date. ${JOIN_DATES_NOTE}`}
                    >
                      Joined
                    </th>
                    <th className="px-3 py-2 text-right font-semibold" title="Creators who first posted in this period, by post date">
                      Posted
                    </th>
                    <th
                      className="px-3 py-2 text-right font-semibold"
                      title="Of the invites logged in this period, the share that have joined since"
                    >
                      Reply → join
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r) => {
                    const links = pipelineLinks(r.team, ranges.current);
                    const rate = joinRate(r.current);
                    return (
                      <tr key={r.team} className="border-t border-border transition-colors hover:bg-accent/40">
                        <td className="px-3 py-2 font-medium">{nameOf(r)}</td>
                        <td className="px-3 py-2 text-right" data-label="Messages">
                          {r.recordsMessages ? (
                            <Figure value={r.current.messages} to={links.messages} delta={countDelta(r.current.messages, r.previous.messages)} />
                          ) : (
                            <Muted title={NO_MESSAGES_HINT}>—</Muted>
                          )}
                        </td>
                        <td className="px-3 py-2 text-right" data-label="Invites">
                          <Figure value={r.current.invites} to={links.invites} delta={countDelta(r.current.invites, r.previous.invites)} />
                        </td>
                        <td className="px-3 py-2 text-right" data-label="Joined">
                          {r.tracksJoins ? (
                            <Figure
                              value={r.current.joinedInPeriod}
                              to={links.joined}
                              delta={
                                joinsComparable ? countDelta(r.current.joinedInPeriod, r.previous.joinedInPeriod) : null
                              }
                            />
                          ) : (
                            <Muted title={HAND_TRACKED_HINT}>Not tracked</Muted>
                          )}
                        </td>
                        <td className="px-3 py-2 text-right" data-label="Posted">
                          {r.tracksJoins ? (
                            <Figure value={r.current.posted} to={links.posted} delta={countDelta(r.current.posted, r.previous.posted)} />
                          ) : (
                            <Muted title={HAND_TRACKED_HINT}>Not tracked</Muted>
                          )}
                        </td>
                        <td className="px-3 py-2 text-right" data-label="Reply → join">
                          {r.tracksJoins && rate !== null ? (
                            <span className="inline-flex items-baseline justify-end gap-1.5">
                              <span className="font-mono tabular-nums">{pct(rate)}</span>
                              <DeltaText delta={rateDelta(rate, joinRate(r.previous))} />
                            </span>
                          ) : (
                            <Muted title={r.tracksJoins ? 'No invites logged in this period' : HAND_TRACKED_HINT}>—</Muted>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </section>
  );
}

function KpiTile({
  icon: Icon,
  label,
  value,
  previous,
  loading,
  hint,
  title,
  noComparison,
  to,
}: {
  icon: LucideIcon;
  label: string;
  /** Null renders "—" (nothing recorded to count). */
  value: number | null;
  /** Null: the period before can't be compared (shows `noComparison`). */
  previous: number | null;
  loading: boolean;
  hint?: string;
  title?: string;
  noComparison?: string;
  /** The list behind the count, when there is one. */
  to?: string;
}): JSX.Element {
  const delta = value === null || previous === null ? null : countDelta(value, previous);
  const change = value === null || previous === null ? null : percentDelta(value, previous);
  const body = (
    <>
      <div className="flex items-center gap-1.5 text-[0.8125rem] font-semibold text-muted-foreground">
        <Icon className="h-3.5 w-3.5 text-brand" strokeWidth={1.85} />
        {label}
      </div>
      <div className="mt-2 font-mono text-[1.625rem] font-medium leading-none tracking-[-0.04em] tabular-nums">
        {loading ? <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" /> : value === null ? '—' : value.toLocaleString()}
      </div>
      <p className="mt-1.5 text-xs text-muted-foreground">
        {loading ? (
          ' '
        ) : delta ? (
          <>
            <DeltaText delta={delta} />
            {change && delta.tone !== 'flat' && <span className={toneClass(delta)}> ({change})</span>} vs the
            period before
          </>
        ) : value !== null && previous === null && noComparison ? (
          noComparison
        ) : (
          (title ?? ' ')
        )}
      </p>
      {hint && <p className="text-xs text-muted-foreground">{hint}</p>}
    </>
  );
  return to ? (
    <Link
      to={to}
      title={title}
      className="panel block rounded-xl p-4 transition-colors hover:bg-accent/60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/50"
    >
      {body}
    </Link>
  ) : (
    <div className="panel rounded-xl p-4" title={title}>
      {body}
    </div>
  );
}

function toneClass(delta: Delta): string {
  return delta.tone === 'up'
    ? 'text-success-strong'
    : delta.tone === 'down'
      ? 'text-destructive-strong'
      : 'text-muted-foreground';
}

function DeltaText({ delta }: { delta: Delta | null }): JSX.Element | null {
  if (!delta) return null;
  return (
    <span className={cn('font-mono text-[11px] tabular-nums', toneClass(delta))} title="Change on the period before">
      {delta.text}
    </span>
  );
}

function Figure({ value, to, delta }: { value: number; to: string; delta: Delta | null }): JSX.Element {
  return (
    <span className="inline-flex items-baseline justify-end gap-1.5">
      <Link to={to} className="font-mono tabular-nums hover:underline focus-visible:underline focus-visible:outline-none">
        {value.toLocaleString()}
      </Link>
      <DeltaText delta={delta} />
    </span>
  );
}

function Muted({ title, children }: { title: string; children: string }): JSX.Element {
  return (
    <span className="cursor-help text-muted-foreground" title={title}>
      {children}
    </span>
  );
}
